import React, { useState } from 'react';
import { Mail as MailIcon, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import Input from '../../ui/input';
import Label from '../../ui/label';
import { Alert, AlertDescription } from '../../ui/alert';
import gameInvitationService from '../../../services/gameInvitationService';

interface InviteUserDialogProps {
  gameID: string;
  currentUsername: string;
  onInviteSent?: () => void;
  children?: React.ReactNode;
}

/**
 * Dialog component for inviting users to a game
 */
const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  gameID,
  currentUsername,
  onInviteSent,
  children,
}) => {
  const [username, setUsername] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await gameInvitationService.sendGameInvitation(
        currentUsername,
        username,
        gameID,
      );
      setSuccess(result.message || 'Invitation sent successfully!');
      setUsername('');
      if (onInviteSent) {
        onInviteSent();
      }
    } catch (err) {
      let errorMessage = 'Failed to send invitation';

      if (err instanceof Error) {
        const rawMessage = err.message;
        if (rawMessage.includes('Error sending game invitation: ')) {
          errorMessage = rawMessage.replace('Error sending game invitation: ', '');
        } else {
          errorMessage = rawMessage;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Invite a Player</DialogTitle>
          <DialogDescription>
            Enter the username of the player you want to invite to this game.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleInviteUser}>
          <div className='grid gap-4 py-4'>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='username' className='text-right'>
                Username
              </Label>
              <Input
                id='username'
                placeholder='Enter username'
                value={username}
                onChange={e => setUsername(e.target.value)}
                className='col-span-3'
              />
            </div>

            {error && (
              <Alert variant='destructive'>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className='bg-green-50 border-green-200'>
                <AlertDescription className='text-green-800'>{success}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button type='submit' disabled={loading || !username.trim()}>
              {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              <MailIcon className='mr-2 h-4 w-4' />
              Send Invitation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InviteUserDialog;
